import { formatReadableNumber } from '@lfg/core/lib';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';

import { colors } from 'src/global/styles/colors';
import { useAnalyticsContext } from 'src/hooks/useAnalyticsContext';
import { useGetAverageSalesChartDataQuery } from 'src/store/services/analytics.service';

import { DEFAULT_TIME_PERIOD, TIME_PERIOD_OPTIONS } from '../constants/timePeriods';

export function AverageSaleChart() {
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_TIME_PERIOD);
  const { address, network } = useAnalyticsContext();

  const {
    data: chartData,
    isLoading,
    error,
  } = useGetAverageSalesChartDataQuery(
    {
      period: selectedPeriod as any,
      size: 50,
      address,
      network,
    },
    { skip: !address || !network },
  );
  const chartSeries = useMemo(() => {
    return Object.entries(chartData?.result || {}).map(([projectId, data]) => ({
      name: projectId,
      data: data.map(item => item.y),
    }));
  }, [chartData]);

  const handlePeriodChange = (newPeriod: string) => {
    setSelectedPeriod(newPeriod);
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: 'line',
      background: 'transparent',
      toolbar: {
        show: false,
      },
      fontFamily: 'Gotham',
    },
    theme: {
      mode: 'dark',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    colors: ['#495DB6', '#7C6DF9', '#4CC3C1', '#F6B936', '#EE7081', '#F83A38'],
    grid: {
      show: true,
      borderColor: 'rgba(255, 255, 255, 0.03)',
      strokeDashArray: 6,
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: Object.values(chartData?.result || {})
        .flat()
        .map(item => item.s),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: true,
        color: 'rgba(255, 255, 255, 0.03)',
      },
      labels: {
        style: {
          colors: colors.white,
          fontSize: '12px',
          fontFamily: 'Gotham',
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      tickAmount: 6,
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham',
          fontWeight: 400,
        },
        formatter: (value: number) => `$${formatReadableNumber(value, { maximumFractionDigits: 2 })}`,
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      theme: 'dark',
      style: {
        fontSize: '12px',
        fontFamily: 'Gotham',
      },
    },
  };

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.white,
            fontFamily: 'Gotham',
          }}>
          Average Sale per Project
        </Typography>

        <FormControl size='small'>
          <Select
            value={selectedPeriod}
            onChange={e => handlePeriodChange(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '4px',
              color: colors.white,
              fontSize: '12px',
              fontFamily: 'Gotham',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-icon': {
                color: colors.white,
              },
            }}>
            {TIME_PERIOD_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 280 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: colors.neutral90 }}>Loading chart data...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: colors.red200, textAlign: 'center' }}>Failed to load chart data</Typography>
          </Box>
        ) : chartData ? (
          <Chart options={chartOptions} series={chartSeries} type='line' height='100%' />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: colors.neutral90, textAlign: 'center' }}>No chart data available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
